import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ProductCard } from "../ProductCard";

import cardStyles from "../../styles/composable/card.module.scss";
import categoryListStyles from "../../styles/composable/category-list.module.scss";
import styles from "./index.module.scss";

type ICategoryCard =
    templates.ProductCategoryListPageTemplate["category_cards"][number];

const CategoryCard = (
    props: Immutable<{ card: ICategoryCard }>,
): React.JSX.Element => {
    return (
        <a
            href={props.card.link}
            className={concatClassNames([cardStyles.card, styles.categoryCard])}
        >
            {props.card.image_url && (
                <img
                    src={props.card.image_url}
                    className={concatClassNames([
                        cardStyles.cardImage,
                        "responsive-img",
                    ])}
                    alt={props.card.title}
                    loading="lazy"
                    decoding="async"
                />
            )}
            <div className={cardStyles.cardContent}>
                {props.card.title && (
                    <div className={cardStyles.cardTitle}>
                        {props.card.title}
                    </div>
                )}
                {props.card.description_html && (
                    <div
                        className={styles.categoryCardDescription}
                        dangerouslySetInnerHTML={{
                            __html: props.card.description_html,
                        }}
                    />
                )}
            </div>
        </a>
    );
};

export const ProductCategoryListPageBody = (
    props: Immutable<templates.ProductCategoryListPageTemplate>,
): React.JSX.Element => {
    return (
        <div className="l-full-width u-margin-bottom--xl">
            <div
                className={concatClassNames([
                    styles.categoryList,
                    "u-full-width-title",
                ])}
            >
                <div
                    className={concatClassNames([
                        categoryListStyles.categoryListHeader,
                        "l-capped-width u-margin-bottom--l",
                    ])}
                >
                    <div>
                        {props.header_copy.map((item, i) =>
                            item.kind === "heading" ? (
                                <a key={i} href={props.by_sleep_position_url}>
                                    <div
                                        className={
                                            categoryListStyles.categoryListHeaderTitle
                                        }
                                    >
                                        {item.value}
                                    </div>
                                </a>
                            ) : (
                                <div
                                    key={i}
                                    className={
                                        styles.categoryListHeaderDescription
                                    }
                                    dangerouslySetInnerHTML={{
                                        __html: item.value,
                                    }}
                                />
                            ),
                        )}
                    </div>
                </div>

                <div className="l-capped-width">
                    <div
                        className={concatClassNames([
                            styles.categoryListSideNav,
                            "l-side-nav",
                        ])}
                    >
                        <div className="category-list-side-nav__mobile-toggle accordion">
                            Shop All
                        </div>
                        <ul
                            className={concatClassNames([
                                styles.categoryListSideNavList,
                                styles.categoryListSideNavListPrimary,
                                "accordion-target",
                            ])}
                        >
                            {props.side_nav.map((l2cat, i) => (
                                <li
                                    className={styles.categoryListSideNavItem}
                                    key={i}
                                >
                                    <a
                                        className={
                                            styles.categoryListSideNavLink
                                        }
                                        href={l2cat.url}
                                    >
                                        {l2cat.name}
                                    </a>
                                    {l2cat.children.length > 0 && (
                                        <ul
                                            className={concatClassNames([
                                                styles.categoryListSideNavList,
                                                styles.categoryListSideNavListSecondary,
                                            ])}
                                        >
                                            {l2cat.children.map((l3cat, j) => (
                                                <li
                                                    key={j}
                                                    className={concatClassNames(
                                                        [
                                                            styles.categoryListSideNavItem,
                                                            styles.categoryListSideNavItemSecondary,
                                                        ],
                                                    )}
                                                >
                                                    <a
                                                        className={concatClassNames(
                                                            [
                                                                styles.categoryListSideNavLink,
                                                                styles.categoryListSideNavLinkSecondary,
                                                            ],
                                                        )}
                                                        href={l3cat.url}
                                                    >
                                                        {l3cat.name}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {props.is_expanded ? (
                        <div className={styles.categoryListContent}>
                            {props.sub_category_groups.map((group, i) => (
                                <React.Fragment key={i}>
                                    <div
                                        className={
                                            styles.categoryListSubCategoryTitle
                                        }
                                    >
                                        {group.title}
                                    </div>
                                    <div
                                        className={concatClassNames([
                                            styles.categoryListSubCategoryList,
                                            "u-flex-container",
                                        ])}
                                    >
                                        {group.hero_image_url && (
                                            <div
                                                className={concatClassNames([
                                                    cardStyles.card,
                                                    styles.subCategoryCard,
                                                    "product-card",
                                                ])}
                                                style={{
                                                    backgroundImage: `url(${group.hero_image_url})`,
                                                }}
                                            ></div>
                                        )}
                                        {group.products.map((product) => (
                                            <ProductCard
                                                key={product.id}
                                                product={product}
                                                className={
                                                    styles.subCategoryCard
                                                }
                                                showPreTitle={false}
                                                showAuxCopy={false}
                                            />
                                        ))}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <div
                            className={concatClassNames([
                                styles.categoryListContent,
                                "l-side-nav-content",
                                "u-flex-container",
                                "l-capped-width",
                            ])}
                        >
                            {props.category_cards.map((card, i) => (
                                <CategoryCard key={i} card={card} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
